import { formatNumber } from "../../../utils/FormattingUtils";
import {
  getCurrentDungeonFloor,
  getExplorationStatus,
  modifyPlayerCurrentHP,
} from "../../exploration/Exploration";
import { ExplorationStatus } from "../../exploration/ExplorationStatus";
import {
  addToInventory,
  calculateInventoryCap,
  getInventory,
} from "../../items/Inventory";
import { ItemStack } from "../../items/Item";
import { getItemById } from "../../items/Items";
import { registerRandomEventTrigger } from "../autorandom/RandomEventTrigger";
import { buildEvent } from "../EventMessageBuilder";
import { EventTag, EventTransform, GameEvent } from "../GameEvent";

export function createChestEvent(
  eventId: string,
  dungeonFloorIds: string[],
  weight: number,
  contents: ItemStack[],
): GameEvent {
  const newChestEventBuilder = buildEvent(eventId, "Found a chest", [
    EventTag.INCONSEQUENTIAL_RANDOM_EVENT,
    EventTag.DO_NOT_AUTO_TRIGGER_IF_ONGOING,
  ]);

  newChestEventBuilder
    .createMessage()
    .setSimpleDescription(
      `As you explore, you happen across a suspicious chest. You think about what you should do...`,
    )
    .addOption(
      "Open the chest (Requires inventory space)",
      {
        transform: (state) => {
          for (let itemStack of contents) {
            const item = getItemById(itemStack.itemOccurrence.itemId);
            state = addToInventory(
              {
                itemId: item.getId(),
                // Needed for equipment not to stack
                params: item.generateCraftedParams(state),
              },
              itemStack.amount,
              state,
            );
          }
          return state;
        },
        isEnabled: (state) =>
          getInventory(state).length + contents.length <=
          calculateInventoryCap(state),
      },
      newChestEventBuilder.createMessage()
        .setSimpleDescription(`You open the chest. You find a little something! You take it with you.
    
**You obtained the following item(s): ${contents
        .map((itemStack) => {
          const item = getItemById(itemStack.itemOccurrence.itemId);
          return `${
            itemStack.amount > 1 ? formatNumber(itemStack.amount) + " " : ""
          }:${item.getId()}:${item.getName(itemStack.itemOccurrence.params)}`;
        })
        .join(", ")}!**`),
    )
    .addOption(
      "Do not open",
      newChestEventBuilder
        .createMessage()
        .setSimpleDescription(
          `You are not sure about the chest and think it might be a trap. You decide to leave it alone...`,
        ),
    )
    .addOption("Decide later");

  const newChestEvent = newChestEventBuilder.build();

  registerRandomEventTrigger(
    newChestEvent,
    weight,
    false,
    (state) =>
      getExplorationStatus(state) != ExplorationStatus.None &&
      dungeonFloorIds.includes(getCurrentDungeonFloor(state)?.getId()),
  );

  return newChestEvent;
}

export function createBadChestEvent(
  eventId: string,
  weight: number,
  lastMessage: string,
  transform: EventTransform,
  dungeonFloorIds?: string[],
): GameEvent {
  const newChestEventBuilder = buildEvent(eventId, "Found a chest", [
    EventTag.INCONSEQUENTIAL_RANDOM_EVENT,
    EventTag.DO_NOT_AUTO_TRIGGER_IF_ONGOING,
  ]);

  newChestEventBuilder
    .createMessage()
    .setSimpleDescription(
      `As you explore, you happen across a suspicious chest. You think about what you should do...`,
    )
    .addOption(
      "Open the chest (Requires inventory space)",
      {
        transform: transform,
        isEnabled: (state) =>
          getInventory(state).length + 1 <= calculateInventoryCap(state),
      },
      newChestEventBuilder.createMessage().setSimpleDescription(lastMessage),
    )
    .addOption(
      "Do not open",
      newChestEventBuilder
        .createMessage()
        .setSimpleDescription(
          `You are not sure about the chest and think it might be a trap. You decide to leave it alone...`,
        ),
    )
    .addOption("Decide later");

  const newChestEvent = newChestEventBuilder.build();

  registerRandomEventTrigger(
    newChestEvent,
    weight,
    false,
    (state) =>
      getExplorationStatus(state) != ExplorationStatus.None &&
      (dungeonFloorIds == null ||
        dungeonFloorIds.includes(getCurrentDungeonFloor(state)?.getId())),
  );

  return newChestEvent;
}

export function loadBadChestEvent() {
  createBadChestEvent(
    "bad_chest_event",
    15,
    `You open the chest. But surprisingly, it has *sharp teeth* on the lid's rim - it is a Mimic! Before you have time to react, it bites you and you scream in pain. It hops away extremely quickly and you lose sight of it, unable to chase after it.

You hold your wounds while you endure the pain, and quickly retreat back to recover...
      
**You lose all your :hp:!**`,
    (state) => {
      state = modifyPlayerCurrentHP(state, -Infinity, "Chest");
      return state;
    },
  );

  createBadChestEvent(
    "empty_chest_event",
    20,
    `You open the chest. You look inside very thoroughly, but you find absolutely nothing. What a disappointment...`,
    (state) => {
      return state;
    },
  );
}
